import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, X, Clock, CheckCircle2, ArrowRightLeft, Trash2, MessageSquare, ChevronDown, ChevronRight } from 'lucide-react';
import { tdlRequirementsApi, usersApi } from '../../services/api';
import { useToast } from '../Toast/Toast';
import { useAuth } from '../../context/AuthContext';

interface Project {
  id: number;
  customer_name: string;
  tdl_name: string;
}

interface Props {
  project: Project;
}

const STATUSES = ['Pending', 'In Progress', 'Completed', 'Transferred', 'Cancelled'];
const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent'];

const EMPTY_NEW = { description: '', assigned_to: '', priority: 'Medium', deadline: '', amount: '' };

const priorityClass = (p: string) =>
  p === 'Urgent' ? 'bg-red-50 text-red-700 border-red-200'
  : p === 'High' ? 'bg-orange-50 text-orange-700 border-orange-200'
  : p === 'Low' ? 'bg-gray-50 text-gray-600 border-gray-200'
  : 'bg-blue-50 text-blue-700 border-blue-200';

const statusClass = (s: string) =>
  s === 'Completed' ? 'bg-green-50 text-green-700 border-green-200'
  : s === 'Cancelled' ? 'bg-gray-100 text-gray-500 border-gray-200'
  : s === 'In Progress' ? 'bg-amber-50 text-amber-700 border-amber-200'
  : s === 'Transferred' ? 'bg-purple-50 text-purple-700 border-purple-200'
  : 'bg-slate-50 text-slate-600 border-slate-200';

const fmtDate = (v: string | null) => (v ? String(v).slice(0, 10) : '—');

/**
 * Type-to-search user picker. Filters the already-loaded staff list in the
 * browser, so there is no request per keystroke. Arrow keys move the
 * highlight, Enter selects, Escape closes and reverts.
 */
const StaffPicker: React.FC<{
  value: string;
  staff: { name: string }[];
  onChange: (name: string) => void;
  exclude?: string | null;
  placeholder?: string;
}> = ({ value, staff, onChange, exclude, placeholder = 'Unassigned' }) => {
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const boxRef = useRef<HTMLDivElement>(null);

  // Keep the visible text in step when the value changes from outside.
  useEffect(() => { setQuery(value); }, [value]);

  // Clicking away closes the list and drops any half-typed text.
  useEffect(() => {
    const onDocDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
        setHighlight(-1);
        setQuery(value);
      }
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [value]);

  const pool = staff.filter(u => u.name !== exclude);
  const q = query.trim().toLowerCase();
  // While the box shows the current selection, list everyone; once the user
  // actually types something different, filter to matches.
  const matches = !q || query === value ? pool : pool.filter(u => u.name.toLowerCase().includes(q));

  const pick = (name: string) => {
    onChange(name);
    setQuery(name);
    setOpen(false);
    setHighlight(-1);
  };

  return (
    <div className="relative" ref={boxRef}>
      <input
        type="text"
        value={query}
        placeholder={placeholder}
        onChange={e => { setQuery(e.target.value); setOpen(true); setHighlight(-1); }}
        onFocus={e => { setOpen(true); e.target.select(); }}
        onKeyDown={e => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
            setHighlight(h => Math.min(h + 1, matches.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight(h => Math.max(h - 1, 0));
          } else if (e.key === 'Enter') {
            if (open && highlight >= 0 && matches[highlight]) {
              e.preventDefault();
              pick(matches[highlight].name);
            }
          } else if (e.key === 'Escape') {
            setOpen(false);
            setHighlight(-1);
            setQuery(value);
          }
        }}
        className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 pr-7 focus:outline-none focus:ring-1 focus:ring-blue-500"
      />
      {value && (
        <button
          type="button"
          title="Clear"
          onMouseDown={e => { e.preventDefault(); pick(''); }}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5"
        >
          <X size={13} />
        </button>
      )}

      {open && (
        <ul className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto bg-white border border-gray-200 rounded shadow-lg text-sm">
          <li
            onMouseDown={e => { e.preventDefault(); pick(''); }}
            className={`px-3 py-1.5 cursor-pointer text-gray-500 italic ${highlight === -1 ? 'bg-gray-50' : 'hover:bg-blue-50'}`}
          >
            Unassigned
          </li>
          {matches.length === 0 ? (
            <li className="px-3 py-1.5 text-gray-400">No match</li>
          ) : (
            matches.map((u, i) => (
              <li
                key={u.name}
                onMouseDown={e => { e.preventDefault(); pick(u.name); }}
                onMouseEnter={() => setHighlight(i)}
                className={`px-3 py-1.5 cursor-pointer text-gray-800 ${i === highlight ? 'bg-blue-100' : 'hover:bg-blue-50'}`}
              >
                {u.name}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};

const TdlRequirementsPanel: React.FC<Props> = ({ project }) => {
  const { showSuccess, showError } = useToast();
  const { canCheckPermission } = useAuth();

  const canAdd = canCheckPermission('tdl', 'add_requirement');
  const canEdit = canCheckPermission('tdl', 'edit');
  const canRemove = canCheckPermission('tdl', 'delete_requirement');

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'active' | 'completed' | 'all'>('active');

  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_NEW });
  const [saving, setSaving] = useState(false);

  const [staff, setStaff] = useState<{ name: string }[]>([]);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [timeline, setTimeline] = useState<Record<number, any[]>>({});
  const [remark, setRemark] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [transferFor, setTransferFor] = useState<any | null>(null);
  const [transferTo, setTransferTo] = useState('');
  const [deleteFor, setDeleteFor] = useState<any | null>(null);
  const [editFor, setEditFor] = useState<any | null>(null);
  const [editForm, setEditForm] = useState({ ...EMPTY_NEW });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await tdlRequirementsApi.getProjectDetail(project.id);
      if (res.success) {
        setRows(res.data.requirements || []);
      } else {
        showError('Error', res.message || 'Failed to load requirements');
      }
    } catch (e: any) {
      showError('Error', e.message || 'Failed to load requirements');
    } finally {
      setLoading(false);
    }
  }, [project.id, showError]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    usersApi.getBasic()
      .then(r => { if (r.success) setStaff(r.data || []); })
      .catch(() => { /* dropdown just stays empty */ });
  }, []);

  const visible = rows.filter(r =>
    filter === 'all' ? true
    : filter === 'completed' ? r.status === 'Completed'
    : r.status !== 'Completed' && r.status !== 'Cancelled');

  const closeAdd = () => { setShowAdd(false); setForm({ ...EMPTY_NEW }); };

  const openEdit = (r: any) => {
    setEditFor(r);
    setEditForm({
      description: r.description || '',
      assigned_to: r.assigned_to || '',
      priority: r.priority || 'Medium',
      deadline: r.deadline ? String(r.deadline).slice(0, 10) : '',
      amount: r.amount != null ? String(r.amount) : '',
    });
  };

  const closeEdit = () => { setEditFor(null); setEditForm({ ...EMPTY_NEW }); };

  const submitEdit = async () => {
    if (!editFor || !editForm.description.trim()) return;
    setSaving(true);
    try {
      const res = await tdlRequirementsApi.updateRequirement(editFor.id, {
        description: editForm.description.trim(),
        assigned_to: editForm.assigned_to || null,
        priority: editForm.priority,
        deadline: editForm.deadline || null,
        amount: editForm.amount === '' ? null : Number(editForm.amount),
      } as any);
      if (res.success) {
        showSuccess('Saved', 'Requirement updated');
        // The timeline gained entries for whatever changed.
        setTimeline(t => { const n = { ...t }; delete n[editFor.id]; return n; });
        closeEdit();
        load();
      } else showError('Error', res.message || 'Failed to update');
    } catch (e: any) {
      showError('Error', e.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const submitNew = async () => {
    if (!form.description.trim()) { showError('Required', 'Description is required'); return; }
    setSaving(true);
    try {
      const res = await tdlRequirementsApi.addRequirement(project.id, {
        description: form.description.trim(),
        assigned_to: form.assigned_to || undefined,
        priority: form.priority,
        deadline: form.deadline || undefined,
        amount: form.amount ? Number(form.amount) : undefined,
      });
      if (res.success) {
        showSuccess('Added', 'Requirement added');
        setForm({ ...EMPTY_NEW });
        setShowAdd(false);
        load();
      } else showError('Error', res.message || 'Failed to add');
    } catch (e: any) {
      showError('Error', e.message || 'Failed to add');
    } finally {
      setSaving(false);
    }
  };

  const openTimeline = async (id: number) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    setRemark('');
    if (!timeline[id]) {
      try {
        const res = await tdlRequirementsApi.getRequirementUpdates(id);
        if (res.success) setTimeline(t => ({ ...t, [id]: res.data || [] }));
      } catch { /* timeline stays empty */ }
    }
  };

  const changeStatus = async (row: any, status: string) => {
    setBusyId(row.id);
    try {
      const res = await tdlRequirementsApi.updateRequirementStatus(row.id, status);
      if (res.success) {
        showSuccess('Updated', res.message || 'Status updated');
        setTimeline(t => { const n = { ...t }; delete n[row.id]; return n; });
        load();
      } else showError('Error', res.message || 'Failed');
    } catch (e: any) {
      showError('Error', e.message || 'Failed');
    } finally {
      setBusyId(null);
    }
  };

  const addRemark = async (row: any) => {
    if (!remark.trim()) return;
    setBusyId(row.id);
    try {
      const res = await tdlRequirementsApi.updateRequirement(row.id, { remark: remark.trim() });
      if (res.success) {
        setRemark('');
        const upd = await tdlRequirementsApi.getRequirementUpdates(row.id);
        if (upd.success) setTimeline(t => ({ ...t, [row.id]: upd.data || [] }));
        load();
      } else showError('Error', res.message || 'Failed');
    } catch (e: any) {
      showError('Error', e.message || 'Failed');
    } finally {
      setBusyId(null);
    }
  };

  const doTransfer = async () => {
    if (!transferFor || !transferTo) return;
    setBusyId(transferFor.id);
    try {
      const res = await tdlRequirementsApi.transferRequirement(transferFor.id, { transfer_to: transferTo });
      if (res.success) {
        showSuccess('Transferred', `Assigned to ${transferTo}`);
        setTransferFor(null);
        setTransferTo('');
        load();
      } else showError('Error', res.message || 'Failed');
    } catch (e: any) {
      showError('Error', e.message || 'Failed');
    } finally {
      setBusyId(null);
    }
  };

  const doDelete = async () => {
    if (!deleteFor) return;
    setBusyId(deleteFor.id);
    try {
      const res = await tdlRequirementsApi.deleteRequirement(deleteFor.id);
      if (res.success) {
        showSuccess('Deleted', 'Requirement removed');
        setDeleteFor(null);
        load();
      } else showError('Error', res.message || 'Failed');
    } catch (e: any) {
      showError('Error', e.message || 'Failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        {/* Header — title, filters and Add all on one row */}
        <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-800 shrink-0">Requirements</h2>

          <div className="flex gap-1">
            {(['active', 'completed', 'all'] as const).map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={`text-xs font-medium px-3 py-1.5 rounded border capitalize transition-colors ${
                  filter === f ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                }`}>
                {f}
              </button>
            ))}
          </div>

          {canAdd && (
            <button onClick={() => setShowAdd(v => !v)}
              className="ml-auto shrink-0 flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700">
              <Plus size={14} /> Add Requirement
            </button>
          )}
        </div>

        {/* List — one row per requirement */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="py-12 text-center text-sm text-gray-500">Loading…</div>
          ) : visible.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-sm text-gray-600">No {filter === 'all' ? '' : filter} requirements yet.</p>
              {canAdd && filter !== 'completed' && (
                <button onClick={() => setShowAdd(true)} className="mt-2 text-sm text-blue-600 hover:underline">
                  Add the first one
                </button>
              )}
            </div>
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 text-xs text-gray-600 uppercase tracking-wide">
                  <th className="py-2.5 px-2 w-8 border border-gray-200"></th>
                  <th className="py-2.5 px-3 text-left w-10 border border-gray-200">#</th>
                  <th className="py-2.5 px-3 text-left border border-gray-200">Requirement</th>
                  <th className="py-2.5 px-3 text-left w-40 border border-gray-200">Assigned To</th>
                  <th className="py-2.5 px-3 text-left w-24 border border-gray-200">Priority</th>
                  <th className="py-2.5 px-3 text-left w-32 border border-gray-200">Deadline</th>
                  <th className="py-2.5 px-3 text-right w-24 border border-gray-200">Amount</th>
                  <th className="py-2.5 px-3 text-left w-32 border border-gray-200">Status</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r, idx) => {
                  const overdue = r.deadline && new Date(r.deadline) < new Date() && r.status !== 'Completed';
                  return (
                    <React.Fragment key={r.id}>
                      <tr className="hover:bg-blue-50/40 align-middle">
                        <td className="py-2 px-2 text-center border border-gray-200">
                          <button onClick={() => openTimeline(r.id)}
                            title={expanded === r.id ? 'Hide activity' : 'Show activity'}
                            className="text-gray-500 hover:text-blue-600 align-middle">
                            {expanded === r.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </button>
                        </td>
                        <td className="py-2 px-3 text-gray-500 tabular-nums border border-gray-200">{idx + 1}</td>
                        <td className="py-2 px-3 border border-gray-200">
                          {canEdit ? (
                            <button
                              onClick={() => openEdit(r)}
                              title="Click to edit this requirement"
                              className="block w-full text-left text-gray-900 truncate max-w-[420px] hover:text-blue-600 hover:underline"
                            >
                              {r.description}
                            </button>
                          ) : (
                            <div className="text-gray-900 truncate max-w-[420px]" title={r.description}>
                              {r.description}
                            </div>
                          )}
                          {r.latest_remark && (
                            <div className="text-xs text-gray-500 truncate max-w-[420px] flex items-center gap-1 mt-0.5"
                              title={r.latest_remark}>
                              <MessageSquare size={11} className="shrink-0" />
                              {r.latest_remark}
                            </div>
                          )}
                        </td>
                        <td className="py-2 px-3 text-gray-800 whitespace-nowrap border border-gray-200">
                          {r.assigned_to || <span className="text-gray-400">Unassigned</span>}
                        </td>
                        <td className="py-2 px-3 border border-gray-200">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${priorityClass(r.priority)}`}>
                            {r.priority}
                          </span>
                        </td>
                        <td className={`py-2 px-3 tabular-nums whitespace-nowrap border border-gray-200 ${overdue ? 'text-red-600 font-semibold' : 'text-gray-700'}`}>
                          {r.deadline
                            ? <span className="flex items-center gap-1 whitespace-nowrap"><Clock size={12} className="shrink-0" /> {fmtDate(r.deadline)}</span>
                            : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="py-2 px-3 text-right font-mono tabular-nums text-gray-800 border border-gray-200">
                          {r.amount != null ? `₹${Number(r.amount).toLocaleString('en-IN')}` : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="py-2 px-3 border border-gray-200">
                          {canEdit ? (
                            <select
                              value={r.status}
                              disabled={busyId === r.id}
                              onChange={e => changeStatus(r, e.target.value)}
                              className={`w-full text-xs font-semibold rounded border px-1.5 py-1 disabled:opacity-50 ${statusClass(r.status)}`}>
                              {STATUSES.map(st => <option key={st} value={st}>{st}</option>)}
                            </select>
                          ) : (
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${statusClass(r.status)}`}>
                              {r.status}
                            </span>
                          )}
                          {r.dev_completed_at && r.status !== 'Completed' && (
                            <div className="text-xs text-purple-600 mt-0.5">dev done — awaiting finalize</div>
                          )}
                        </td>
                      </tr>

                      {/* Activity timeline */}
                      {expanded === r.id && (
                        <tr>
                          <td colSpan={8} className="border border-gray-200 bg-gray-50 px-5 py-3">
                            {canEdit && (
                              <div className="flex gap-2 mb-3">
                                <input
                                  value={remark}
                                  onChange={e => setRemark(e.target.value)}
                                  onKeyDown={e => { if (e.key === 'Enter') addRemark(r); }}
                                  placeholder="Add a remark…"
                                  className="flex-1 text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                                <button onClick={() => addRemark(r)} disabled={busyId === r.id || !remark.trim()}
                                  className="text-sm px-3 py-1.5 rounded bg-gray-700 text-white hover:bg-gray-800 disabled:opacity-40">
                                  Post
                                </button>
                              </div>
                            )}
                            {(canEdit || canRemove) && (
                              <div className="flex items-center gap-2 mb-3 pb-3 border-b border-gray-200">
                                {canEdit && r.status !== 'Completed' && (
                                  <button onClick={() => changeStatus(r, 'Completed')} disabled={busyId === r.id}
                                    className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded border border-green-200 bg-green-50 text-green-700 hover:bg-green-100 disabled:opacity-50">
                                    <CheckCircle2 size={15} /> Mark complete
                                  </button>
                                )}
                                {canEdit && (
                                  <button onClick={() => { setTransferFor(r); setTransferTo(''); }}
                                    className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded border border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100">
                                    <ArrowRightLeft size={15} /> Transfer
                                  </button>
                                )}
                                {canRemove && (
                                  <button onClick={() => setDeleteFor(r)}
                                    className="ml-auto flex items-center gap-1.5 text-sm px-3 py-1.5 rounded border border-red-200 bg-red-50 text-red-600 hover:bg-red-100">
                                    <Trash2 size={15} /> Delete
                                  </button>
                                )}
                              </div>
                            )}
                            {(timeline[r.id] || []).length === 0 ? (
                              <p className="text-sm text-gray-500 py-1">No activity yet.</p>
                            ) : (
                              <ul className="space-y-1.5">
                                {(timeline[r.id] || []).map(u => (
                                  <li key={u.id} className="text-sm">
                                    <span className="font-semibold text-gray-800">{u.update_type}</span>
                                    {u.content && <span className="text-gray-700"> — {u.content}</span>}
                                    {!u.content && u.old_value != null && (
                                      <span className="text-gray-700"> — {u.old_value} → {u.new_value}</span>
                                    )}
                                    <span className="text-gray-500"> · {u.created_by} · {String(u.created_at).replace('T', ' ').slice(0, 16)}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

      </div>

      {/* Add requirement dialog */}
      {showAdd && canAdd && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4"
          onClick={closeAdd}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-800">Add Requirement</h3>
              <button onClick={closeAdd} className="text-gray-400 hover:text-gray-600 p-1"><X size={18} /></button>
            </div>

            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Requirement <span className="text-red-500">*</span>
                </label>
                <textarea
                  autoFocus
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="What is required?"
                  rows={3}
                  className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Assign to</label>
                  <StaffPicker
                    value={form.assigned_to}
                    staff={staff}
                    onChange={name => setForm({ ...form, assigned_to: name })}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
                  <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}
                    className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500">
                    {PRIORITIES.map(pr => <option key={pr} value={pr}>{pr}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Deadline</label>
                  <input type="date" value={form.deadline} onChange={e => setForm({ ...form, deadline: e.target.value })}
                    className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Amount</label>
                  <input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })}
                    placeholder="0" className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200">
              <button onClick={closeAdd}
                className="text-sm px-4 py-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={submitNew} disabled={saving || !form.description.trim()}
                className="text-sm px-4 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit requirement dialog */}
      {editFor && canEdit && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4"
          onClick={closeEdit}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-800">Edit Requirement</h3>
              <button onClick={closeEdit} className="text-gray-400 hover:text-gray-600 p-1"><X size={18} /></button>
            </div>

            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Requirement <span className="text-red-500">*</span>
                </label>
                <textarea
                  autoFocus
                  value={editForm.description}
                  onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                  rows={3}
                  className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Assign to</label>
                  <StaffPicker
                    value={editForm.assigned_to}
                    staff={staff}
                    onChange={name => setEditForm({ ...editForm, assigned_to: name })}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
                  <select value={editForm.priority} onChange={e => setEditForm({ ...editForm, priority: e.target.value })}
                    className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500">
                    {PRIORITIES.map(pr => <option key={pr} value={pr}>{pr}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Deadline</label>
                  <input type="date" value={editForm.deadline} onChange={e => setEditForm({ ...editForm, deadline: e.target.value })}
                    className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Amount</label>
                  <input type="number" value={editForm.amount} onChange={e => setEditForm({ ...editForm, amount: e.target.value })}
                    placeholder="0" className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
              </div>

              <p className="text-xs text-gray-500">
                Status is changed from the table; every edit here is recorded in the activity trail.
              </p>
            </div>

            <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200">
              <button onClick={closeEdit}
                className="text-sm px-4 py-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={submitEdit} disabled={saving || !editForm.description.trim()}
                className="text-sm px-4 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer prompt */}
      {transferFor && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-5">
            <h3 className="text-sm font-semibold text-gray-800 mb-1">Transfer requirement</h3>
            <p className="text-xs text-gray-500 mb-3 line-clamp-2">{transferFor.description}</p>
            <select value={transferTo} onChange={e => setTransferTo(e.target.value)}
              className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 mb-4">
              <option value="">Select a user…</option>
              {staff.filter(u => u.name !== transferFor.assigned_to).map(u => (
                <option key={u.name} value={u.name}>{u.name}</option>
              ))}
            </select>
            <div className="flex justify-end gap-2">
              <button onClick={() => setTransferFor(null)}
                className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={doTransfer} disabled={!transferTo || busyId === transferFor.id}
                className="text-xs px-3 py-1.5 rounded bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50">
                Transfer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteFor && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-5">
            <h3 className="text-sm font-semibold text-gray-800 mb-1">Delete requirement?</h3>
            <p className="text-xs text-gray-500 mb-4">
              “{deleteFor.description}” and its activity trail will be removed permanently.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteFor(null)}
                className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={doDelete} disabled={busyId === deleteFor.id}
                className="text-xs px-3 py-1.5 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TdlRequirementsPanel;
